import type { FC } from 'react';
import type { StatusCellProps } from './StatusCell.types';
import { Icon, Flex, Tooltip, Text } from '@/_components';
import { StatusTooltipContent } from './components/StatusTooltipContent';
import { getClasses } from '@/_utils/classnames';
import styles from './StatusCell.module.css';

export const StatusCell: FC<StatusCellProps> = ({
  count,
  currentStep,
  incompleteSections,
  questionnaire,
  total,
}) => {
  const {
    completed: isCompleted,
    locked: isLocked,
    is_draft: isDraft,
    in_progress: isInProgress,
    opened_timestamp: openedTimestamp,
  } = questionnaire;

  const statusCellClasses: string = getClasses(
    styles.statusCell__indicator__bar,
    {
      [styles.statusCell__indicator__barCompleted]: isCompleted,
      [styles.statusCell__indicator__barEmpty]: !isCompleted && total === 0,
    },
  );

  const getWidth = (): number => {
    if (isCompleted) return 100;

    const stepTotal = total ?? 0;
    const stepCount = count ?? 0;
    const percentageComplete: number = Math.round(
      (stepCount / stepTotal) * 100,
    );

    return percentageComplete || (isInProgress && 5) || 0;
  };

  const getStatusText = (): string => {
    if (isCompleted) return 'Completed';
    if (isDraft) return 'Draft';
    // TODO(API): @chris Incorrect state from backend, should be empty to not mix state handling
    if (currentStep && currentStep !== 'In Progress') return currentStep;
    if (isInProgress) return 'In Progress';
    if (!!openedTimestamp) return 'Opened';

    return 'Sent';
  };

  const status: string = getStatusText();
  const hasIncompleteSections: boolean = !!incompleteSections?.length;

  return (
    <Tooltip isDisabled={!hasIncompleteSections}>
      <Tooltip.Content>
        <StatusTooltipContent
          incompleteSections={incompleteSections}
          title={status}
        />
      </Tooltip.Content>
      <Tooltip.Trigger>
        <div className={styles.statusCell}>
          <div className={styles.statusCell__indicator}>
            <div
              className={statusCellClasses}
              style={{ width: `${getWidth()}%` }}
            />
          </div>
          <Flex
            isYCentered={true}
            isXCentered={true}
            gap={6}
            className="wp-100"
          >
            {isLocked && (
              <Icon name="lock" size={20} className={styles.statusCell__icon} />
            )}
            <Text
              type="tiny"
              className={styles.statusCell__label}
              color={isCompleted ? 'success' : isDraft ? 'accent' : 'primary'}
            >
              {status}
            </Text>
          </Flex>
        </div>
      </Tooltip.Trigger>
    </Tooltip>
  );
};
